import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { CheckoutModal } from '../components/CheckoutModal';
import { CheckoutContent, useCommerce, type Commerce } from './ShopCommerce';
import { CartLineList } from './CartLines';
import { CartStatus, EmptyCart } from './CartStates';
import { FreeShippingBar } from './FreeShippingBar';
import { CartSuggestions } from './CartSuggestions';
import { OrderNote } from './OrderNote';
import { useGoToCheckout } from './useGoToCheckout';
import './cartDrawer.css';
import { shopWords } from './shopWords';
import { CartEstimate } from './CartEstimate';

// The cart as Sleek draws it: a full-height panel from the side with the lines, what they come to and the way
// on to checkout. A shop whose order form has a page of its own checks out there, as Shopify's do; the rest
// fill the form in here, one step on from the cart, with the way back to it at the top.

// Names the drawer's step, with the number of items beside "Your cart" as Sleek shows it.
const DrawerHeading: React.FC<{ title: string; count: number }> = ({ title, count }) => (
  <h2 className="xe-cart-heading">{title}{count > 0 ? <span className="xe-cart-heading-count" aria-hidden="true">{count}</span> : null}</h2>
);

// Draws the foot of the cart: the order note, what it comes to so far, where delivery is settled, and the two ways on.
const CartFooter: React.FC<{ commerce: Commerce; onCheckout: () => void }> = ({ commerce, onCheckout }) => (
  <div className="xe-cart-foot">
    <OrderNote commerce={commerce} />
    <CartStatus commerce={commerce} />
    <CartEstimate commerce={commerce} />
    <p className="xe-cart-note">{shopWords(commerce.language).deliveryAtCheckout}</p>
    <div className="xe-cart-actions">
      <a className="xe-cart-secondary" href="/cart">{shopWords(commerce.language).viewCart}</a>
      <button type="button" className="xe-cart-primary" disabled={!commerce.quote || commerce.loading || commerce.error !== ''} onClick={onCheckout}>{shopWords(commerce.language).checkOut}</button>
    </div>
  </div>
);

// Draws the cart's first step: its lines, then the total and the way on to checkout.
const CartStep: React.FC<{ commerce: Commerce }> = ({ commerce }) => {
  const goToCheckout = useGoToCheckout(commerce);
  if (!commerce.lines.length) return <EmptyCart onContinue={() => commerce.show('closed')} />;
  return (
    <>
      <div className="xe-cart-body"><FreeShippingBar commerce={commerce} /><CartLineList commerce={commerce} editable /><CartSuggestions commerce={commerce} /></div>
      <CartFooter commerce={commerce} onCheckout={goToCheckout} />
    </>
  );
};

// Draws the cart's second step: the way back to the cart, then the order form for what it holds. Buy it now's form
// orders one product apart from the cart, so as on Shopify it offers no way back to a cart it never came from.
const CheckoutStep: React.FC<{ commerce: Commerce }> = ({ commerce }) => (
  <div className="xe-cart-body">
    {!commerce.direct && <button type="button" className="xe-cart-back" onClick={() => commerce.show('cart')}><ArrowLeft size={16} aria-hidden="true" />{shopWords(commerce.language).backToCart}</button>}
    <CheckoutContent inDrawer />
  </div>
);

// Mounts the cart drawer for this visit; it opens on Add to cart, on the bag in the header, and on /cart.
export const CartDrawer: React.FC = () => {
  const commerce = useCommerce();
  if (!commerce) return null;
  const words = shopWords(commerce.language);
  const title = commerce.view === 'checkout' ? words.checkout : words.yourCart;
  const count = commerce.view === 'cart' ? commerce.lines.reduce((sum, line) => sum + line.quantity, 0) : 0;
  return (
    <CheckoutModal open={commerce.view !== 'closed'} placement="drawer" title={count ? `${title}, ${count} ${count === 1 ? words.item : words.itemsCount}` : title} closeLabel={words.close}
      onClose={() => commerce.show('closed')} heading={<DrawerHeading title={title} count={count} />}>
      {commerce.view === 'checkout' ? <CheckoutStep commerce={commerce} /> : <CartStep commerce={commerce} />}
    </CheckoutModal>
  );
};
